<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import AddLabel from 'shared/components/ui/dropdown/AddLabel.vue';
import PanelEmptyState from '../sidebar/PanelEmptyState.vue';

export default {
  components: {
    PanelEmptyState,
    Spinner,
    LabelDropdown,
    AddLabel,
  },
  setup() {
    const { isAdmin } = useAdmin();

    const {
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
    } = useConversationLabels();

    const showSearchDropdownLabel = ref(false);
    const isCreateLabelModalOpen = ref(false);

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      if (isCreateLabelModalOpen.value) {
        return;
      }

      showSearchDropdownLabel.value = false;
    };

    const forceCloseDropdownLabel = () => {
      isCreateLabelModalOpen.value = false;
      showSearchDropdownLabel.value = false;
    };

    const onToggleCreateLabelModal = isOpen => {
      isCreateLabelModalOpen.value = isOpen;
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value && !isCreateLabelModalOpen.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);
    return {
      isAdmin,
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      showSearchDropdownLabel,
      isCreateLabelModalOpen,
      closeDropdownLabel,
      forceCloseDropdownLabel,
      onToggleCreateLabelModal,
      toggleLabels,
    };
  },
  data() {
    return {
      selectedLabels: [],
    };
  },

  computed: {
    ...mapGetters({
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
  },
};
</script>

<template>
  <div
    :class="{ 'sidebar-labels-wrap--dropdown-open': showSearchDropdownLabel }"
    class="sidebar-labels-wrap"
  >
    <div
      v-if="!conversationUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap"
        @keyup.esc="closeDropdownLabel"
      >
        <!-- Sem etiqueta a seção mostraria só o botão de somar, que lê como
             "nada aqui" sem dizer. A moldura tracejada é a mesma dos outros
             vazios do painel (#1243). -->
        <PanelEmptyState
          v-if="!activeLabels.length"
          :message="$t('CONTACT_PANEL.SIDEBAR_SECTIONS.NO_CONVERSATION_LABELS')"
          :action-label="$t('CONTACT_PANEL.SIDEBAR_SECTIONS.ADD_LABEL')"
          @action="toggleLabels"
        />
        <template v-else>
          <AddLabel @add="toggleLabels" />
          <woot-label
            v-for="label in activeLabels"
            :key="label.id"
            :title="label.title"
            :description="label.description"
            show-close
            :color="label.color"
            variant="smooth"
            class="max-w-[calc(100%-0.5rem)]"
            @click="removeLabelFromConversation"
          />
        </template>

        <div class="dropdown-wrap">
          <div
            :class="{ 'dropdown-pane--open': showSearchDropdownLabel }"
            class="dropdown-pane"
          >
            <LabelDropdown
              v-if="showSearchDropdownLabel"
              :account-labels="accountLabels"
              :selected-labels="savedLabels"
              :allow-creation="isAdmin"
              @add="addLabelToConversation"
              @remove="removeLabelFromConversation"
              @close-dropdown="forceCloseDropdownLabel"
              @toggle-create-modal="onToggleCreateLabelModal"
            />
          </div>
        </div>
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  position: relative;
  margin-bottom: 0;

  &.sidebar-labels-wrap--dropdown-open {
    z-index: 60;
  }
}
.contact-conversation--list {
  width: 100%;

  .label-wrap {
    @apply relative leading-6;
    line-height: var(--space-medium);

    .dropdown-wrap {
      @apply absolute w-full top-7;

      .dropdown-pane {
        @apply box-border w-full;
      }
    }
  }
}

.error {
  color: var(--r-500);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
}
</style>
